/**
 * SettingsPage
 * 
 * Unified settings page with tabbed navigation:
 * - Account: Profile info, sign out (all users)
 * - Subscription: Plan management, billing, usage (team lead + admin)
 * - Branding: Logo, colors, fonts, team URL (team lead + admin)
 */

import React, { useState, useCallback, useMemo } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useAdminPreview } from '../contexts/AdminPreviewContext';
import { useData } from '../contexts/DataContext';
import { db } from '../firebaseInit';
import { normalizeEmailToDocId } from '../utils/emailNormalization';
import { getUserPath, getArtifactProjectId } from '../utils/firestorePaths';
import Breadcrumbs from '../components/shared/Breadcrumbs';
import SubscriptionManager from '../components/SubscriptionManager';
import TeamBrandingSettings from '../components/TeamBrandingSettings';

const SettingsPage = ({
  onLogout,
}) => {
  const { userEmail, userRole, userTeam, userTeamName, user, claims } = useAuth();
  const { effectiveIsAdmin: isAdmin, effectiveIsTeamLead: isTeamLead } = useAdminPreview();
  const { userDefaults, refreshUserDefaults } = useData();

  const [activeTab, setActiveTab] = useState('account');
  const [isEditingName, setIsEditingName] = useState(false);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);
  const [nameError, setNameError] = useState('');

  const handleEditName = useCallback(() => {
    setEditFirstName(userDefaults?.firstName || '');
    setEditLastName(userDefaults?.lastName || '');
    setNameError('');
    setIsEditingName(true);
  }, [userDefaults]);

  const handleCancelEditName = useCallback(() => {
    setIsEditingName(false);
    setNameError('');
  }, []);

  const handleSaveName = useCallback(async () => {
    if (!editFirstName.trim() && !editLastName.trim()) {
      setNameError('Please enter at least a first or last name.');
      return;
    }
    setIsSavingName(true);
    setNameError('');
    try {
      const appId = getArtifactProjectId();
      const emailDocId = normalizeEmailToDocId(user.email);
      const teamId = claims?.teamId || userTeam;
      const userPath = getUserPath(appId, teamId, emailDocId);
      await updateDoc(doc(db, userPath), {
        firstName: editFirstName.trim(),
        lastName: editLastName.trim(),
      });
      await refreshUserDefaults();
      setIsEditingName(false);
    } catch (err) {
      console.error('❌ Error saving name:', err);
      setNameError('Could not save. Please check your connection and try again.');
    } finally {
      setIsSavingName(false);
    }
  }, [editFirstName, editLastName, user, claims, userTeam, refreshUserDefaults]);

  const tabLabel = useMemo(() => {
    switch (activeTab) {
      case 'account': return 'Profile';
      case 'subscription': return 'Subscription';
      case 'branding': return 'Branding';
      default: return null;
    }
  }, [activeTab]);

  // Tab button renderer to keep JSX DRY
  const TabButton = useCallback(({ id, label, visible = true }) => {
    if (!visible) return null;
    return (
      <button
        onClick={() => setActiveTab(id)}
        className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
          activeTab === id
            ? 'bg-blue-100 text-blue-700 border-b-2 border-blue-600'
            : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
        }`}
      >
        {label}
      </button>
    );
  }, [activeTab]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Page Title - matching sidebar header height */}
      <div className="flex items-center justify-between border-b border-gray-200 px-4 h-14 bg-white">
        <h2 className="text-xl font-semibold text-blue-600 border-b-2 border-blue-600 pb-1">Settings</h2>
      </div>

      {/* Breadcrumbs */}
      <div className="bg-white px-4 pt-1.5">
        <Breadcrumbs items={[
          { label: 'Home', href: '/call-script' },
          { label: 'Settings', href: '/settings' },
          ...(tabLabel ? [{ label: tabLabel, current: true }] : [])
        ]} />
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 border-b border-gray-200 bg-white px-4">
        <TabButton id="account" label="Profile" />
        <TabButton id="subscription" label="Subscription" visible={isAdmin || isTeamLead} />
        <TabButton id="branding" label="Branding" visible={isAdmin || isTeamLead} />
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-auto p-4 bg-gray-50">

        {/* Account Tab */}
        {activeTab === 'account' && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
              <div className="p-4 space-y-4">
                {/* Profile Section */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Your Profile</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* First Name */}
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-sm text-gray-500">First Name</div>
                        {!isEditingName && (
                          <button
                            onClick={handleEditName}
                            aria-label="Edit first and last name"
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 transition-colors"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Edit
                          </button>
                        )}
                      </div>
                      {isEditingName ? (
                        <input
                          type="text"
                          value={editFirstName}
                          onChange={(e) => setEditFirstName(e.target.value)}
                          placeholder="First name"
                          className="w-full px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                          aria-label="First name"
                          autoFocus
                        />
                      ) : (
                        <div className="font-medium text-gray-900">
                          {userDefaults?.firstName || <span className="text-gray-400 italic">Not set</span>}
                        </div>
                      )}
                    </div>

                    {/* Last Name */}
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-sm text-gray-500">Last Name</div>
                        {isEditingName && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={handleSaveName}
                              disabled={isSavingName}
                              className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded hover:bg-blue-700 disabled:opacity-50 transition-colors font-medium"
                              aria-label="Save name"
                            >
                              {isSavingName ? 'Saving…' : 'Save'}
                            </button>
                            <button
                              onClick={handleCancelEditName}
                              disabled={isSavingName}
                              className="text-xs text-gray-500 hover:text-gray-700 font-medium transition-colors"
                              aria-label="Cancel editing name"
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                      {isEditingName ? (
                        <input
                          type="text"
                          value={editLastName}
                          onChange={(e) => setEditLastName(e.target.value)}
                          placeholder="Last name"
                          className="w-full px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                          aria-label="Last name"
                          onKeyDown={(e) => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') handleCancelEditName(); }}
                        />
                      ) : (
                        <div className="font-medium text-gray-900">
                          {userDefaults?.lastName || <span className="text-gray-400 italic">Not set</span>}
                        </div>
                      )}
                      {isEditingName && nameError && (
                        <p className="mt-1 text-xs text-red-600" role="alert">{nameError}</p>
                      )}
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="text-sm text-gray-500 mb-1">Email</div>
                      <div className="font-medium text-gray-900">{userEmail || 'Not available'}</div>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="text-sm text-gray-500 mb-1">Role</div>
                      <div className="font-medium text-gray-900">
                        {isAdmin ? 'Super Admin' : isTeamLead ? 'Team Lead' : 'Agent'}
                      </div>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="text-sm text-gray-500 mb-1">Team</div>
                      <div className="font-medium text-gray-900">{userTeamName || userTeam || 'No team'}</div>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="text-sm text-gray-500 mb-1">Team ID</div>
                      <div className="font-medium text-gray-900 font-mono text-sm">{userTeam || 'N/A'}</div>
                    </div>
                  </div>
                </div>

                {/* Sign Out */}
                <div className="border-t border-gray-200 pt-4">
                  <button
                    onClick={onLogout}
                    className="flex items-center space-x-3 p-4 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                  >
                    <div className="bg-red-100 p-2 rounded">
                      <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <div className="font-medium text-red-700">Sign Out</div>
                      <div className="text-sm text-red-500">Sign out of your account</div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Subscription Tab */}
        {activeTab === 'subscription' && (isAdmin || isTeamLead) && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
              <SubscriptionManager />
            </div>
          </div>
        )}

        {/* Branding Tab */}
        {activeTab === 'branding' && (isAdmin || isTeamLead) && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
              <div className="p-4">
                <TeamBrandingSettings />
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default SettingsPage;
